import React, { useEffect, useState } from "react";
import { Badge, Button, Form, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import "sweetalert2/src/sweetalert2.scss";

const isDefaultGroup = (group) => group.toLowerCase() === "default";

const PLATFORMS = [
  { value: "domain", label: "Domain" },
  { value: "website", label: "Website" },
  { value: "social-media", label: "Social Media" },
  { value: "e-commerce", label: "E-Commerce" },
  { value: "app-store", label: "App Store" },
  { value: "tmch", label: "TMCH" },
];

// same list as the server; the first is the default
const INPUT_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "documents", label: "Documents" },
];

const DOCUMENTS = "documents";
const MAX_DOCUMENTS = 5;
// only these may show as a dropdown filter to clients
const DROPDOWN_TYPES = ["text", "number"];

const typeLabel = (column) => {
  const label = INPUT_TYPES.find((type) => type.value === column.inputType)?.label || column.inputType;
  if (column.inputType === DOCUMENTS) return `${label} (up to ${column.maxFiles || 1})`;
  return column.dropdown ? `${label}, dropdown` : label;
};

const emptyColumn = () => ({ name: "", inputType: INPUT_TYPES[0].value, maxFiles: 1, dropdown: false, platforms: [] });

function DropdownCheck({ id, checked, onChange }) {
  return <Form.Check type="checkbox" id={id} label="Dropdown filter for clients" checked={checked} onChange={(event) => onChange(event.target.checked)} />;
}

const platformLabel = (value) => PLATFORMS.find((platform) => platform.value === value)?.label || value;

const errorText = (error) => error.response?.data?.message || "Something went wrong.";

const codeName = (code) => `${code.value} - ${code.label}${code.isActive ? "" : " (inactive)"}`;

// group name -> its codes, custom groups only
const groupRows = (rows, aclType) => {
  const groups = new Map();
  rows
    .filter((row) => row.aclType === aclType && !isDefaultGroup(row.group))
    .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }))
    .forEach((row) => groups.set(row.group, [...(groups.get(row.group) || []), row]));

  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
};

// Ticking a group selects all of its codes, unticking removes them all; a single code can be removed or added back.
function GroupList({ title, noun, idPrefix, groups, selected, onChange, emptyHint }) {
  return (
    <Form.Group>
      <Form.Label>{title}</Form.Label>
      {!groups.length && <Form.Text className="text-muted">{emptyHint}</Form.Text>}
      {groups.map(([name, codes]) => {
        const ids = codes.map((code) => code._id);
        const chosen = codes.filter((code) => selected.includes(code._id));
        const all = chosen.length === codes.length;
        const toggleGroup = () => onChange(all ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
        const remove = (id) => onChange(selected.filter((item) => item !== id));
        const add = (id) => onChange([...selected, id]);

        return (
          <div key={name} className="border rounded p-2 mb-2">
            <Form.Check
              type="checkbox"
              id={`${idPrefix}-${name}`}
              label={`${name} - ${chosen.length} of ${codes.length} ${noun}`}
              checked={all}
              ref={(input) => {
                if (input) input.indeterminate = chosen.length > 0 && !all;
              }}
              onChange={toggleGroup}
            />
            {chosen.length > 0 && (
              <div className="mt-1" style={{ maxHeight: 110, overflowY: "auto" }}>
                {codes.map((code) =>
                  selected.includes(code._id) ? (
                    <Badge key={code._id} variant="secondary" className="mr-1 mb-1" title={code.label}>
                      {codeName(code)}
                      <span
                        role="button"
                        aria-label={`Remove ${codeName(code)}`}
                        title="Remove"
                        className="ml-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => remove(code._id)}
                      >
                        &times;
                      </span>
                    </Badge>
                  ) : (
                    <Badge
                      key={code._id}
                      variant="secondary"
                      role="button"
                      aria-label={`Add ${codeName(code)}`}
                      title="Add back"
                      className="mr-1 mb-1"
                      style={{ backgroundColor: "#fff", color: "#6c757d", border: "1px dashed #adb5bd", cursor: "pointer" }}
                      onClick={() => add(code._id)}
                    >
                      + {codeName(code)}
                    </Badge>
                  ),
                )}
              </div>
            )}
          </div>
        );
      })}
    </Form.Group>
  );
}

function CompanyPermissionsModal({ company, onHide, onSaved, alert }) {
  const [statusGroups, setStatusGroups] = useState([]);
  const [reasonGroups, setReasonGroups] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newColumn, setNewColumn] = useState(emptyColumn());
  const [addingColumn, setAddingColumn] = useState(false);
  const [editingColumn, setEditingColumn] = useState("");

  useEffect(() => {
    setNewColumn(emptyColumn());
    setAddingColumn(false);
    setEditingColumn("");
    if (!company) {
      setForm(null);
      return;
    }

    const permissions = company.permissions || {};
    setForm({
      apiAccess: !!permissions.apiAccess,
      status: permissions.status || [],
      reasonCode: permissions.reasonCode || [],
      customColumns: (permissions.customColumns || []).map(({ name, inputType, platforms, maxFiles, dropdown }) => ({
        name,
        inputType: inputType || INPUT_TYPES[0].value,
        platforms: [...(platforms || [])],
        ...(inputType === DOCUMENTS ? { maxFiles: maxFiles || 1 } : {}),
        ...(DROPDOWN_TYPES.includes(inputType || INPUT_TYPES[0].value) ? { dropdown: !!dropdown } : {}),
      })),
    });

    window.axios
      .get("/acl")
      .then(({ data }) => {
        const status = groupRows(data, "StatusCode");
        const reason = groupRows(data, "ReasonCode");
        setStatusGroups(status);
        setReasonGroups(reason);

        // a code deleted since the company was given it would fail the save
        const existing = (groups) => new Set(groups.flatMap(([, codes]) => codes.map((code) => code._id)));
        const stillThere = existing(status);
        const stillThereReason = existing(reason);
        setForm((current) =>
          current && {
            ...current,
            status: current.status.filter((id) => stillThere.has(id)),
            reasonCode: current.reasonCode.filter((id) => stillThereReason.has(id)),
          },
        );
      })
      .catch((error) => alert.show(errorText(error), { type: "error" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const toggleNewPlatform = (platform) =>
    setNewColumn((current) => ({
      ...current,
      platforms: current.platforms.includes(platform) ? current.platforms.filter((item) => item !== platform) : [...current.platforms, platform],
    }));

  const closeAddColumn = () => {
    setNewColumn(emptyColumn());
    setAddingColumn(false);
  };

  const addColumn = () => {
    const name = newColumn.name.trim();
    if (!/^[A-Za-z][A-Za-z0-9 _-]*$/.test(name)) {
      alert.show("Column name must start with a letter and use letters, numbers, spaces, - or _.", { type: "error" });
      return;
    }
    if (!newColumn.platforms.length) {
      alert.show("Choose at least one platform for the column.", { type: "error" });
      return;
    }
    if (form.customColumns.some((column) => column.name.toLowerCase() === name.toLowerCase())) {
      alert.show(`Column "${name}" already exists.`, { type: "error" });
      return;
    }

    const { maxFiles, dropdown, ...column } = newColumn;
    setField("customColumns", [
      ...form.customColumns,
      {
        ...column,
        name,
        ...(column.inputType === DOCUMENTS ? { maxFiles } : {}),
        ...(DROPDOWN_TYPES.includes(column.inputType) ? { dropdown } : {}),
      },
    ]);
    closeAddColumn();
  };

  // a column the company already has may hold values on its records; removing it deletes them on save
  // An existing column keeps its name and type; its platforms and dropdown flag can change.
  const setColumnDropdown = (column, dropdown) =>
    setField(
      "customColumns",
      form.customColumns.map((item) => (item.name === column.name ? { ...item, dropdown } : item)),
    );

  const toggleColumnPlatform = (column, platform) => {
    const platforms = column.platforms.includes(platform) ? column.platforms.filter((item) => item !== platform) : [...column.platforms, platform];
    if (!platforms.length) {
      alert.show("A column needs at least one platform. Use Remove to delete the column.", { type: "error" });
      return;
    }

    setField(
      "customColumns",
      form.customColumns.map((item) => (item.name === column.name ? { ...item, platforms } : item)),
    );
  };

  const removeColumn = async (column) => {
    const saved = (company.permissions?.customColumns || []).some((item) => item.name === column.name);
    if (saved) {
      const result = await Swal.fire({
        title: `Remove "${column.name}"?`,
        text: "When you save, every value of this column is deleted from this company's records. This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, remove",
      });
      if (!result.isConfirmed) return;
    }

    setField(
      "customColumns",
      form.customColumns.filter((item) => item.name !== column.name),
    );
  };

  const save = async () => {
    if (saving) return;

    setSaving(true);
    try {
      const response = await window.axios.put(`/company/${company.id || company._id}/permissions`, {
        apiAccess: form.apiAccess,
        status: form.status,
        reasonCode: form.reasonCode,
        customColumns: form.customColumns,
      });
      alert.show("Permissions saved.");
      onSaved(response.data.permissions);
      onHide();
    } catch (error) {
      alert.show(errorText(error), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={!!company} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ color: "#d32f2f" }}>Permissions{company ? ` - ${company.name}` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {form && (
          <Form>
            <Form.Group>
              <Form.Label>Access</Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  id="perm-api-access"
                  label="API access"
                  checked={form.apiAccess}
                  onChange={(event) => setField("apiAccess", event.target.checked)}
                />
              </div>
            </Form.Group>

            <GroupList
              title="Status groups"
              noun="statuses"
              idPrefix="perm-status-group"
              groups={statusGroups}
              selected={form.status}
              onChange={(ids) => setField("status", ids)}
              emptyHint="No groups yet. Create one in Status & Reason (Group)."
            />

            <GroupList
              title="Reason code groups"
              noun="reason codes"
              idPrefix="perm-reason-group"
              groups={reasonGroups}
              selected={form.reasonCode}
              onChange={(ids) => setField("reasonCode", ids)}
              emptyHint="No groups yet. Create one in Status & Reason (Group)."
            />
            <Form.Text className="text-muted">Default groups apply to every company, so they are not listed here.</Form.Text>

            <Form.Group className="mt-3">
              <Form.Label>Custom columns</Form.Label>
              {form.customColumns.map((column) => (
                <div key={column.name} className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{column.name}</strong>
                    <span className="text-muted ml-2">{typeLabel(column)}</span>
                    {editingColumn === column.name ? (
                      <div>
                        {PLATFORMS.map((platform) => (
                          <Form.Check
                            inline
                            key={platform.value}
                            type="checkbox"
                            id={`perm-custom-edit-${column.name}-${platform.value}`}
                            label={platform.label}
                            checked={column.platforms.includes(platform.value)}
                            onChange={() => toggleColumnPlatform(column, platform.value)}
                          />
                        ))}
                        <Form.Text className="text-muted">
                          Unticking a platform deletes this column's values{column.inputType === DOCUMENTS ? " and files" : ""} on that platform when you save.
                        </Form.Text>
                        {DROPDOWN_TYPES.includes(column.inputType) && (
                          <DropdownCheck id={`perm-custom-dropdown-${column.name}`} checked={!!column.dropdown} onChange={(checked) => setColumnDropdown(column, checked)} />
                        )}
                      </div>
                    ) : (
                      <div>
                        {column.platforms.map((platform) => (
                          <Badge key={platform} variant="secondary" className="mr-1">
                            {platformLabel(platform)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-nowrap ml-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="mr-2"
                      aria-label={`Edit platforms of ${column.name}`}
                      onClick={() => setEditingColumn(editingColumn === column.name ? "" : column.name)}
                    >
                      {editingColumn === column.name ? "Done" : "Edit platforms"}
                    </Button>
                    <Button size="sm" variant="outline-danger" aria-label={`Remove column ${column.name}`} onClick={() => removeColumn(column)}>
                      Remove
                    </Button>
                  </div>
                </div>
              ))}

              {!addingColumn && (
                <div>
                  <Button size="sm" variant="outline-primary" onClick={() => setAddingColumn(true)}>
                    + Add column
                  </Button>
                </div>
              )}

              {addingColumn && (
              <div className="border rounded p-2">
                <div className="d-flex mb-2">
                  <Form.Control
                    id="perm-custom-name"
                    placeholder="Column name"
                    value={newColumn.name}
                    onChange={(event) => setNewColumn({ ...newColumn, name: event.target.value })}
                  />
                  <Form.Control
                    as="select"
                    id="perm-custom-type"
                    aria-label="Input type"
                    className="ml-2"
                    style={{ maxWidth: 140 }}
                    value={newColumn.inputType}
                    onChange={(event) => setNewColumn({ ...newColumn, inputType: event.target.value })}
                  >
                    {INPUT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Control>
                  {newColumn.inputType === DOCUMENTS && (
                    <Form.Control
                      as="select"
                      id="perm-custom-max-files"
                      aria-label="How many documents"
                      title="How many documents"
                      className="ml-2"
                      style={{ maxWidth: 150 }}
                      value={newColumn.maxFiles}
                      onChange={(event) => setNewColumn({ ...newColumn, maxFiles: Number(event.target.value) })}
                    >
                      {Array.from({ length: MAX_DOCUMENTS }, (_, index) => index + 1).map((count) => (
                        <option key={count} value={count}>
                          {count} document{count === 1 ? "" : "s"}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                  <Button className="ml-2" onClick={addColumn}>
                    Add
                  </Button>
                  <Button variant="secondary" className="ml-2" onClick={closeAddColumn}>
                    Cancel
                  </Button>
                </div>
                {PLATFORMS.map((platform) => (
                  <Form.Check
                    inline
                    key={platform.value}
                    type="checkbox"
                    id={`perm-custom-platform-${platform.value}`}
                    label={platform.label}
                    checked={newColumn.platforms.includes(platform.value)}
                    onChange={() => toggleNewPlatform(platform.value)}
                  />
                ))}
                {DROPDOWN_TYPES.includes(newColumn.inputType) && (
                  <DropdownCheck id="perm-custom-dropdown" checked={newColumn.dropdown} onChange={(checked) => setNewColumn({ ...newColumn, dropdown: checked })} />
                )}
                <Form.Text className="text-muted">Give the column a name, pick its input type (Text if left as is) and the platforms it is for, then Add. For Documents, also choose how many files a record can hold (up to 5; PDF, XLS, XLSX or CSV).</Form.Text>
              </div>
              )}
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button onClick={save} disabled={saving || !form}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CompanyPermissionsModal;
